import { motion } from "framer-motion";
import { WAITLIST_URL, scrollToId } from "./data";

const Line = ({ children, delay }) => (
    <span className="block overflow-hidden pb-1">
        <motion.span
            className="block"
            initial={{ y: "110%" }}
            animate={{ y: 0 }}
            transition={{ duration: 0.9, delay, ease: [0.22, 1, 0.36, 1] }}
        >
            {children}
        </motion.span>
    </span>
);

export const Hero = () => (
    <section id="top" data-testid="hero-section" className="relative px-5 pt-32 sm:px-8 sm:pt-40">
        <div className="mx-auto max-w-[1500px]">
            <motion.p
                data-testid="hero-status-badge"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.8, delay: 0.1 }}
                className="font-mono2 text-xs uppercase tracking-[0.2em] text-[#C7B7FF]"
            >
                An early research project · Working title: Discovery
            </motion.p>

            <h1
                data-testid="hero-headline"
                className="mt-8 max-w-5xl text-4xl font-bold leading-[1.06] tracking-tight text-[#F4F1EC] sm:text-6xl lg:text-7xl"
            >
                <Line delay={0.2}>Find people</Line>
                <Line delay={0.29}>
                    you{" "}
                    <em className="font-serif2 font-normal italic text-[#C7B7FF]">actually</em>
                </Line>
                <Line delay={0.38}>want to know.</Line>
            </h1>

            <motion.p
                data-testid="hero-subtitle"
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.7, delay: 0.55, ease: [0.22, 1, 0.36, 1] }}
                className="mt-8 max-w-xl text-base leading-relaxed text-[#C9C5C0] sm:text-lg"
            >
                A social discovery concept built around the things that actually make people
                interesting to each other — interests, taste, obsessions, personality and context.
            </motion.p>

            <motion.p
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.7, delay: 0.65 }}
                className="font-mono2 mt-4 text-xs uppercase tracking-[0.18em] text-[#8A8580]"
            >
                Not another swipe-first dating app.
            </motion.p>

            <motion.div
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.7, delay: 0.75, ease: [0.22, 1, 0.36, 1] }}
                className="mt-10 flex flex-wrap items-center gap-5"
            >
                <a
                    href={WAITLIST_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="hero-waitlist-btn"
                    className="cta-primary rounded-full bg-[#C7B7FF] px-7 py-3.5 text-base font-semibold text-[#0D0C0B]"
                >
                    Join the early waitlist <span className="cta-arrow">→</span>
                </a>
                <a
                    href="#how-it-works"
                    data-testid="hero-how-it-works-link"
                    onClick={(e) => scrollToId(e, "#how-it-works")}
                    className="nav-link text-base text-[#C9C5C0] hover:text-[#F4F1EC]"
                >
                    See how it works ↓
                </a>
            </motion.div>

            <motion.p
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.7, delay: 0.85 }}
                className="mt-6 text-sm text-[#8A8580]"
            >
                Early research project · No spam · Help shape the first version
            </motion.p>

            <motion.figure
                initial={{ opacity: 0, scale: 0.985 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 1.1, delay: 0.5, ease: [0.22, 1, 0.36, 1] }}
                className="frame mx-auto mt-16 max-w-[1400px] overflow-hidden rounded-sm sm:mt-24"
            >
                <img
                    src="/images/hero-collage.jpg"
                    srcSet="/images/hero-collage-sm.jpg 900w, /images/hero-collage.jpg 1600w"
                    sizes="(max-width: 768px) 100vw, 1400px"
                    alt="Discovery Project concept collage — several fictional profiles with their interests, current obsessions, questions and glimpses, showing many ways to understand a person"
                    fetchPriority="high"
                    className="block h-auto w-full"
                />
            </motion.figure>
        </div>
    </section>
);
